import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PageHeader from '../../components/PageHeader';
import Select from 'react-select';
import { format } from 'date-fns';
import Swal from 'sweetalert2';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { debounce } from '../../utils/debounce';
import { useNavigate } from 'react-router-dom';

const LMS = process.env.REACT_APP_API_LMS;
const TOKEN = process.env.REACT_APP_API_TOKEN;
const AWS = process.env.REACT_APP_AWS_HOST;
const INTERNSHIP_ASSIGNMENT_ID = 101;

const REVIEW_STATUS_OPTIONS = [
  { value: 'Review Pending', label: 'Review Pending' },
  { value: 'Reviewed', label: 'Reviewed' }
];

const IFStudentAssignmentPage = () => {
  const Navigate = useNavigate();
  const [batchId, setBatchId] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReviewStatus, setSelectedReviewStatus] = useState(null);

  // Modals
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [showGiveMarksModal, setShowGiveMarksModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [rubrics, setRubrics] = useState([]);
  const [evaluationScores, setEvaluationScores] = useState({});
  const [totalScore, setTotalScore] = useState('');
  const [remark, setRemark] = useState('');

  // Get batch_id from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const batch = params.get('batch');
    if (batch) setBatchId(batch);
  }, []);

  // Load Students
  const loadStudents = async () => {
    if (!batchId) return;
    try {
      const res = await fetch(`${LMS}/api/get-students-by-batch/${batchId}`, {
        headers: { Authorization: TOKEN }
      });
      const data = await res.json();
      if (data.status) {
        const opts = data.data.map(s => ({
          value: s.student_id,
          label: `${s.first_name} ${s.last_name}`
        }));
        setStudents(opts);
      }
    } catch (err) { console.error(err); }
  };

  // Load Assignments
  const loadAssignments = async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      const payload = {
        batch_id: parseInt(batchId),
        student_id: selectedStudent?.value,
        search: searchQuery || undefined
      };

      const res = await fetch(`${LMS}/api/get-assignment`, {
        method: 'POST',
        headers: {
          Authorization: TOKEN,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await res.json();
      if (result.status) {
        setAssignments(result.data.assignments);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced version of loadAssignments
  const loadAssignmentsRef = useRef(loadAssignments);
  useEffect(() => { loadAssignmentsRef.current = loadAssignments; });
  const debouncedLoadAssignments = useMemo(() => debounce(() => {
    loadAssignmentsRef.current();
  }, 500), []);

  useEffect(() => { loadStudents(); }, [batchId]);

  useEffect(() => {
    if (batchId) {
      debouncedLoadAssignments();
    }
  }, [batchId, selectedStudent, searchQuery]);

  // Open Assignment Details Modal
  const openAssignmentDetails = async (assignment) => {
    setSelectedAssignment(assignment);
    setShowAssignmentModal(true);

    // Fetch full assignment details
    try {
      const res = await fetch(
        `${LMS}/api/get-assignment-details?student_id=${assignment.student_id}&assignment_id=${assignment.assignment_id}`,
        {
          method: 'GET',
          headers: {
            Authorization: TOKEN
          }
        }
      );
      const data = await res.json();
      if (data.data?.success) {
        setSelectedAssignment(prev => ({ ...prev, details: data.data.result }));
      }
    } catch (err) { console.error(err); }
  };

  // Open Student Answer Modal
  const openAnswer = (submission) => {
    setSelectedAnswer(submission);
    setShowAnswerModal(true);
  };

  // Open Give Marks
  const openGiveMarks = async (submission) => {
    setSelectedSubmission(submission);
    setShowGiveMarksModal(true);
    await loadRubrics(submission);
  };

  // Open Review Marks
  const openReviewMarks = async (submission) => {
    setSelectedSubmission(submission);
    setShowReviewModal(true);
    await loadRubrics(submission, true);
  };

  const loadRubrics = async (submission, isReview = false) => {
    try {
      const res = await fetch(`${LMS}/api/rubrics`, {
        method: 'POST',
        headers: {
          Authorization: TOKEN,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rubric_ids: submission.rubrics_id,
          assignment_id: submission.assignment_id
        })
      });
      const data = await res.json();
      const fetchedRubrics = data.rubrics || [];

      const scores = {};
      let total = 0;
      let comment = '';

      if (isReview && submission.evaluation) {
        Object.keys(submission.evaluation.rubrics).forEach(cId => {
          const levelId = submission.evaluation.rubrics[cId];
          const rubric = fetchedRubrics.find(r => r.id == cId);
          if (rubric) {
            const level = rubric.levels.find(l => l.id == levelId);
            if (level) {
              scores[cId] = levelId;
              total += level.level_score;
            }
          }
        });
        comment = submission.evaluation.remark || '';
      } else {
        fetchedRubrics.forEach(r => {
          const selected = r.levels.find(l => l.is_selected);
          if (selected) {
            scores[r.id] = selected.id;
            total += selected.level_score;
          }
        });
      }

      setRubrics(fetchedRubrics);
      setEvaluationScores(scores);
      setTotalScore(isReview && submission.evaluation && submission.evaluation.score !== undefined ? submission.evaluation.score : '');
      setRemark(comment);
    } catch (err) { console.error(err); }
  };

  const handleRubricChange = (criterionId, levelId, score) => {
    const newScores = { ...evaluationScores, [criterionId]: levelId };
    setEvaluationScores(newScores);
  };

  const submitEvaluation = async () => {
    const payload = {
      student_id: selectedSubmission.student_id,
      batch_id: selectedSubmission.batch_id,
      course_id: selectedSubmission.course_id,
      assignment_id: selectedSubmission.assignment_id,
      submission_id: selectedSubmission.submission_id || selectedSubmission.id,
      rubrics: evaluationScores,
      score: totalScore,
      remark: remark
    };

    let timerInterval;
    Swal.fire({
      title: 'Submitting marks...',
      html: 'Please wait while we save your evaluation.',
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const url = showReviewModal && selectedSubmission.evaluation?.id
        ? `${LMS}/api/rubrics-evaluations/${selectedSubmission.evaluation.id}`
        : `${LMS}/api/rubrics-evaluations`;

      await fetch(url, {
        method: showReviewModal && selectedSubmission.evaluation?.id ? 'PUT' : 'POST',
        headers: {
          Authorization: TOKEN,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Marks submitted successfully!',
        timer: 2000,
        showConfirmButton: false
      });

      closeAllModals();
      loadAssignments();
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Failed',
        text: 'Failed to submit marks. Please try again.',
        timer: 2000,
        showConfirmButton: false
      });
    } finally {
      clearInterval(timerInterval);
    }
  };

  const closeAllModals = () => {
    setShowAssignmentModal(false);
    setShowAnswerModal(false);
    setShowGiveMarksModal(false);
    setShowReviewModal(false);
    setEvaluationScores({});
    setTotalScore(0);
    setRemark('');
  };

  const formatDate = (date) => format(new Date(date), 'dd MMM yyyy, hh:mm a');

  const getReviewStatus = (item) => {
    if (Number(item?.is_evaluation_submitted) === 1) return 'Reviewed';
    if (Number(item?.is_submitted) === 1) return 'Review Pending';
    return 'Not Submitted';
  };

  const filteredAssignments = useMemo(() => {
    if (!selectedReviewStatus?.value) return assignments;
    return assignments.filter((a) => getReviewStatus(a) === selectedReviewStatus.value);
  }, [assignments, selectedReviewStatus]);

  const handleReset = () => {
    setSelectedStudent(null);
    setSearchQuery('');
    setSelectedReviewStatus(null);
  };

  // Render course title with conditional tooltip
  const renderCourseTitle = (courseTitle) => {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{courseTitle}</Tooltip>}
      >
        <small
          className="d-inline-block text-truncate"
          style={{ maxWidth: '150px', cursor: 'pointer' }}
        >
          {courseTitle}
        </small>
      </OverlayTrigger>
    );
  };

  return (
    <>
      <PageHeader title="Student Assignments" breadcrumbs={[
        { label: "Dashboard", path: "/teacher-dashboard" },
        { label: "Batch", path: '/teacher-batches' },
        { label: "Assignments", active: true }
      ]} />

      {/* Filters */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label fw-semibold">Student</label>
              <Select
                options={students}
                value={selectedStudent}
                onChange={setSelectedStudent}
                placeholder="All Students"
                isClearable
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search student, assignment, content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Review Status</label>
              <Select
                options={REVIEW_STATUS_OPTIONS}
                value={selectedReviewStatus}
                onChange={setSelectedReviewStatus}
                placeholder="All Review Status"
                isClearable
              />
            </div>
            <div className="col-md-3 col-lg-2">
              <button className="btn btn-outline-secondary" onClick={handleReset}>
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments Table/Cards */}
      <div className="card skyblueborder">
        <div className="card-header skyblue-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0 text-primary">
            <i className="fas fa-tasks me-2"></i>
            Assignment Submissions
          </h5>
          <Button
            variant="link"
            className="p-0 text-decoration-none text-primary fw-semibold"
            onClick={() => Navigate('/teacher-batches')}
          >
            <i className="fas fa-arrow-left me-1"></i> Back to Batch List
          </Button>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : filteredAssignments.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fa-3x mb-3"></i>
              <h6>No submissions found</h6>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="table-responsive d-none d-lg-block">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light text-dark">
                    <tr>
                      <th className="ps-4">Student</th>
                      <th>Course</th>
                      <th>Assignment</th>
                      <th className="text-center">Student Answer</th>
                      <th className="text-center">Student File</th>
                      <th>Submitted</th>
                      <th>Reviewed</th>
                      <th>Score</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssignments.map((a) => (
                      <tr key={a.id || a.submission_id} className="border-start border-3 border-white">
                        <td className="ps-4">
                          <div className="fw-semibold">{a.first_name} {a.last_name}</div>
                        </td>
                        <td>
                          {renderCourseTitle(a.course_title)}
                        </td>
                        <td>
                          <button
                            className="btn btn-link text-primary p-0 text-start fw-semibold"
                            onClick={() => openAssignmentDetails(a)}
                          >
                            {a.assignment_title}
                          </button>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openAnswer(a)}
                          >
                            View Answer
                          </button>
                        </td>
                        <td className="text-center">
                          {a.assignment_id === INTERNSHIP_ASSIGNMENT_ID ? (
                            <>
                              {a.material_file ? (
                                <button
                                  className="btn btn-sm btn-success d-block w-100 mb-1"
                                  onClick={() => window.open(`${AWS}${a.material_file}`, '_blank')}
                                >
                                  View Observation
                                </button>
                              ) : (
                                <span className="text-muted small d-block mb-1">No Observation</span>
                              )}
                              {a.attendance_sheet ? (
                                <button
                                  className="btn btn-sm btn-info d-block w-100"
                                  onClick={() => window.open(`${AWS}${a.attendance_sheet}`, '_blank')}
                                >
                                  View Attendance
                                </button>
                              ) : (
                                <span className="text-muted small d-block">No Attendance</span>
                              )}
                            </>
                          ) : (
                            <>
                              {a.material_file ? (
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() => window.open(`${AWS}${a.material_file}`, '_blank')}
                                >
                                  View File
                                </button>
                              ) : (
                                <span className="text-muted small">No file</span>
                              )}
                              {a.self_declaration && (
                                <button
                                  className="btn btn-sm btn-info mt-1"
                                  onClick={() => window.open(`${AWS}${a.self_declaration}`, '_blank')}
                                >
                                  View SD
                                </button>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          {a.is_submitted === 1 ? (
                            <>
                              <span className="badge bg-success d-block mb-1">Submitted</span>
                              <small className="text-muted">{formatDate(a.created_at)}</small>
                            </>
                          ) : (
                            <span className="badge bg-secondary">Not Submitted</span>
                          )}
                        </td>
                        <td>
                          {a.is_evaluation_submitted === 1 ? (
                            <span className="badge bg-success">Reviewed</span>
                          ) : a.is_submitted === 1 ? (
                            <span className="badge bg-warning text-dark">Review Pending</span>
                          ) : (
                            <span className="badge bg-secondary">Not Submitted</span>
                          )}
                        </td>
                        <td>
                          {a.is_evaluation_submitted === 1 && a.evaluation?.score !== undefined ? (
                            <span className="fw-bold">{a.evaluation.score}</span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          {a.is_evaluation_submitted === 1 ? (
                            <button className="btn btn-sm btn-info" onClick={() => openReviewMarks(a)}>
                              Review Marks
                            </button>
                          ) : a.is_submitted === 1 ? (
                            <button className="btn btn-sm btn-warning" onClick={() => openGiveMarks(a)}>
                              Give Marks
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-lg-none">
                {filteredAssignments.map((a) => (
                  <div key={a.id || a.submission_id} className="border-bottom p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <div>
                          <div>Student: <span className="fw-bold text-primary mb-1">{a.first_name} {a.last_name}</span></div>
                          <div>Course: <span className="text-muted">{a.course_title}</span></div>
                        </div>
                      </div>
                      {a.is_evaluation_submitted === 1 && a.evaluation?.score !== undefined && (
                        <span className="badge bg-primary fs-6">{a.evaluation.score}</span>
                      )}
                    </div>

                    <button
                      className="btn btn-link text-primary p-0 text-start fw-semibold mb-2"
                      onClick={() => openAssignmentDetails(a)}
                    >
                      {a.assignment_title}
                    </button>

                    <div className="mb-2">
                      <div className="d-flex gap-2 mb-2">
                        {a.is_submitted === 1 ? (
                          <>
                            <span className="badge bg-success">Submitted</span>
                            <small className="text-muted align-self-center">{formatDate(a.created_at)}</small>
                          </>
                        ) : (
                          <span className="badge bg-secondary">Not Submitted</span>
                        )}
                      </div>
                      <div>
                        {a.is_evaluation_submitted === 1 ? (
                          <span className="badge bg-success">Reviewed</span>
                        ) : a.is_submitted === 1 ? (
                          <span className="badge bg-warning text-dark">Review Pending</span>
                        ) : (
                          <span className="badge bg-secondary">Not Submitted</span>
                        )}
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openAnswer(a)}
                      >
                        <i className="fas fa-eye me-1"></i> Answer
                      </button>

                      {a.assignment_id === INTERNSHIP_ASSIGNMENT_ID ? (
                        <>
                          {a.material_file && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => window.open(`${AWS}${a.material_file}`, '_blank')}
                            >
                              <i className="fas fa-file me-1"></i> View Obs.
                            </button>
                          )}
                          {a.attendance_sheet && (
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => window.open(`${AWS}${a.attendance_sheet}`, '_blank')}
                            >
                              <i className="fas fa-file-alt me-1"></i> View Att.
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {a.material_file && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => window.open(`${AWS}${a.material_file}`, '_blank')}
                            >
                              <i className="fas fa-file me-1"></i> File
                            </button>
                          )}
                          {a.self_declaration && (
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => window.open(`${AWS}${a.self_declaration}`, '_blank')}
                            >
                              <i className="fas fa-file-contract me-1"></i> View SD
                            </button>
                          )}
                        </>
                      )}

                      {a.is_evaluation_submitted === 1 ? (
                        <button className="btn btn-sm btn-info" onClick={() => openReviewMarks(a)}>
                          <i className="fas fa-edit me-1"></i> Review
                        </button>
                      ) : a.is_submitted === 1 ? (
                        <button className="btn btn-sm btn-warning" onClick={() => openGiveMarks(a)}>
                          <i className="fas fa-check me-1"></i> Give Marks
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Assignment Details Modal */}
      {showAssignmentModal && selectedAssignment && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-primary pb-3">
                <h5 className="modal-title text-white">Assignment Details</h5>
                <button className="btn-close btn-close-white" onClick={closeAllModals}></button>
              </div>
              <div className="modal-body">
                <h4 className="text-primary mb-3">{selectedAssignment.assignment_title}</h4>
                {selectedAssignment.details?.description && (
                  <div className="bg-light p-4 rounded mb-4" dangerouslySetInnerHTML={{ __html: selectedAssignment.details.description }} />
                )}
                {selectedAssignment.details?.rubrics?.length > 0 && (
                  <div>
                    <h6 className="fw-bold mb-3">Evaluation Rubrics</h6>
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '25%' }}>Criterion</th>
                            {selectedAssignment.details.rubrics[0].levels.map(l => (
                              <th key={l.id}>
                                <span className="fw-bold text-success">
                                  {l.level_name} ({l.level_score})
                                </span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAssignment.details.rubrics.map(r => (
                            <tr key={r.id}>
                              <td className="fw-semibold bg-light">{r.name}</td>
                              {r.levels.map(l => (
                                <td key={l.id}>
                                  <div className="small text-muted">{l.description}</div>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeAllModals}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student Answer Modal */}
      {showAnswerModal && selectedAnswer && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Student Submission</h5>
                <button className="btn-close" onClick={closeAllModals}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <div className="bg-light p-4 rounded" dangerouslySetInnerHTML={{ __html: selectedAnswer.content || '<p>No content submitted</p>' }} />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeAllModals}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rubric Evaluation Modal */}
      {(showGiveMarksModal || showReviewModal) && selectedSubmission && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white pb-3">
                <h5 className="modal-title text-white">Rubric Evaluation</h5>
                <button className="btn-close btn-close-white" onClick={closeAllModals}></button>
              </div>
              <div className="modal-body">
                <div className="bg-light p-3 rounded mb-3 row row-cols-1 row-cols-md-2 g-2">
                  <div className="col"><strong>Student:</strong> {selectedSubmission.first_name} {selectedSubmission.last_name}</div>
                  <div className="col"><strong>Assignment:</strong> {selectedSubmission.assignment_title}</div>
                </div>

                {rubrics.map((rubric) => (
                  <div key={rubric.id} className="mb-3">
                    <h6 className="fw-bold text-primary">{rubric.name}</h6>
                    <div className="ps-3">
                      {rubric.levels.map((level) => {
                        const isChecked = evaluationScores[rubric.id] === level.id ||
                          (!evaluationScores[rubric.id] && level.is_selected);

                        return (
                          <div className="form-check mb-3" key={level.id}>
                            <input
                              className="form-check-input"
                              type="radio"
                              name={`rubric-${rubric.id}`}
                              id={`level-${level.id}`}
                              checked={isChecked}
                              onChange={() => handleRubricChange(rubric.id, level.id, level.level_score)}
                            />
                            <label className="form-check-label" htmlFor={`level-${level.id}`}>
                              <span className="fw-bold text-success">{level.level_name}</span> — {level.description}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <div className="row g-2 align-items-center mb-2">
                  <div className="col-md-6 col-sm-12">
                    <label className="form-label fw-semibold mb-1">Enter Marks</label>
                    <input
                      type="number"
                      className="form-control"
                      value={totalScore}
                      onChange={(e) => {
                        const v = e.target.value;
                        setTotalScore(v === '' ? '' : Number(v));
                      }}
                      placeholder="Enter marks"
                      min="0"
                    />
                  </div>
                  <div className="col-md-6 col-sm-12 align-self-end">
                    <div className="p-2 bg-primary text-white rounded fs-5 fw-bold text-center h-100 d-flex align-items-center justify-content-center">
                      Total Marks Given: {totalScore}
                    </div>
                  </div>
                </div>

                <div className="mt-2">
                  <label className="form-label fw-semibold">Comment (Optional)</label>
                  <textarea className="form-control" rows="4" value={remark} onChange={e => setRemark(e.target.value)}></textarea>
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={closeAllModals}>Cancel</button>
                <button className="btn btn-success px-4" onClick={submitEvaluation}>
                  Submit Marks
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IFStudentAssignmentPage;
